import {useEffect, useMemo, useState} from 'react'
import {createPortal} from 'react-dom'
import {Modal} from 'react-bootstrap'
import {useToast} from '../../../../../components/Toats'
import {KTSVG} from '../../../../../../../_metronic/helpers'
import {getFile} from '../../../../../../services/files'
import {DateComplete} from '../../../../../../utils/date'
import {initialLetters} from '../../../../../../utils/initialLetters'
import {getContrastColor} from '../../../../../../utils/contratColor'
import styles from './modalDetailsCreate.module.css'

type Props = {
  data: any
  show: boolean
  handleClose: () => void
}

const VISION_PRIMARY = '#ffc700'
const modalsRoot = document.getElementById('root-modals') || document.body

const isHttpUrl = (value?: string | null) => Boolean(value && /^https?:\/\//i.test(value))

const ModalDetailsCreate = ({show, handleClose, data}: Props) => {
  const {toggleToast} = useToast()
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [copiedField, setCopiedField] = useState<string | null>(null)

  const company = data?.companies?.[0]
  const primaryColor = company?.color_primary || VISION_PRIMARY
  const contrastColor = getContrastColor(primaryColor)
  const companyName = company?.corporateName || 'Não identificado'
  const initials = initialLetters(companyName || 'EM') || 'EM'
  const createdAt = company?.created_at ? DateComplete(new Date(company.created_at)) : 'Não identificado'
  const cnpj = company?.cnpj || 'Não identificado'
  const planName = company?.planosCompany?.plano || 'Não identificado'
  const premiumCount = company?.planosCompany?.qtd_plants_plano ?? 0
  const starterCount = company?.planosCompany?.qtd_plants_plano_starter ?? 0
  const baseCount = company?.planosCompany?.qtd_plants_gratuita ?? 0
  const userName = data?.name || 'Não identificado'
  const userEmail = data?.email || 'Não identificado'
  const userPassword = data?.senha || ''
  const accountType = data?.account_type
  const companyStatus = company?.status || data?.status

  const imageKey = useMemo(() => {
    if (isHttpUrl(company?.image_url)) return null
    return company?.image || company?.image_url || null
  }, [company?.image, company?.image_url])

  useEffect(() => {
    let active = true

    const loadLogo = async () => {
      if (!show) {
        if (active) setLogoUrl(null)
        return
      }

      if (isHttpUrl(company?.image_url)) {
        if (active) setLogoUrl(company.image_url)
        return
      }

      if (!imageKey) {
        if (active) setLogoUrl(null)
        return
      }

      try {
        const response = await getFile(imageKey)
        if (active) setLogoUrl(response?.url || null)
      } catch {
        if (active) setLogoUrl(null)
      }
    }

    loadLogo()

    return () => {
      active = false
    }
  }, [show, company?.image_url, imageKey])

  const copyValue = async (value: string, field: string, label: string) => {
    if (!value) return

    try {
      await navigator.clipboard.writeText(value)
      setCopiedField(field)
      toggleToast(`Copiado: ${label}.`, 'success')
      window.setTimeout(() => setCopiedField((current) => (current === field ? null : current)), 1800)
    } catch {
      toggleToast('Não foi possível copiar.', 'error')
    }
  }

  return createPortal(
    <Modal
      id='kt_modal_create_app'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName={`modal-dialog modal-dialog-centered mw-650px ${styles.dialog}`}
      show={show}
      onHide={handleClose}
      backdrop={true}
    >
      <div className={styles.header}>
        <div className={styles.headerCopy}>
          <h3 className={styles.title}>Empresa criada</h3>
          <p className={styles.subtitle}>Cadastro concluído. Confira os dados de acesso abaixo.</p>
        </div>
        <button type='button' className={`btn btn-sm btn-icon btn-active-color-primary ${styles.close}`} onClick={handleClose}>
          <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
        </button>
      </div>

      <div className={styles.body}>
        <div className={styles.hero} style={{background: primaryColor, color: contrastColor}}>
          <div className={styles.heroPattern} />
          <div className={styles.logoWrap}>
            {logoUrl ? (
              <img className={styles.logo} src={logoUrl} alt={`Logo de ${companyName}`} />
            ) : (
              <div className={styles.logoFallback}>
                <span className={styles.logoInitials}>{initials}</span>
              </div>
            )}
          </div>
          <div className={styles.heroText} style={{color: contrastColor}}>
            <h4 className={styles.companyName} style={{color: contrastColor}}>
              {companyName}
            </h4>
            <p className={styles.meta} style={{color: contrastColor}}>
              {cnpj} · {createdAt}
            </p>
          </div>
          <div className={styles.badges}>
            {planName && planName !== 'Não identificado' && <span className={styles.badge}>{planName}</span>}
            {accountType && <span className={styles.badge}>{accountType}</span>}
            {companyStatus && <span className={styles.badge}>{companyStatus}</span>}
          </div>
        </div>

        <section className={styles.section}>
          <h5 className={styles.sectionTitle}>Usinas no plano</h5>
          <div className={styles.planGrid}>
            <div className={styles.planCard}>
              <span className={styles.planValue}>{premiumCount}</span>
              <span className={styles.planLabel}>Premium</span>
            </div>
            <div className={styles.planCard}>
              <span className={styles.planValue}>{starterCount}</span>
              <span className={styles.planLabel}>Starter</span>
            </div>
            <div className={styles.planCard}>
              <span className={styles.planValue}>{baseCount}</span>
              <span className={styles.planLabel}>Básica</span>
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <h5 className={styles.sectionTitle}>Acesso do usuário</h5>
          <div className={styles.accessList}>
            <div className={styles.accessRow}>
              <div className={styles.accessCopy}>
                <span className={styles.accessLabel}>Nome</span>
                <span className={styles.accessValue}>{userName}</span>
              </div>
            </div>
            <div className={styles.accessRow}>
              <div className={styles.accessCopy}>
                <span className={styles.accessLabel}>E-mail</span>
                <span className={styles.accessValue}>{userEmail}</span>
              </div>
              {data?.email && (
                <button
                  type='button'
                  className={`btn btn-sm btn-light-primary ${styles.copyBtn}`}
                  onClick={() => copyValue(data.email, 'email', 'e-mail')}
                >
                  {copiedField === 'email' ? 'Copiado' : 'Copiar'}
                </button>
              )}
            </div>
            <div className={styles.accessRow}>
              <div className={styles.accessCopy}>
                <span className={styles.accessLabel}>Senha</span>
                <span className={`${styles.accessValue} ${styles.password}`}>
                  {userPassword || 'Enviada por e-mail'}
                </span>
              </div>
              {userPassword && (
                <button
                  type='button'
                  className={`btn btn-sm btn-light-primary ${styles.copyBtn}`}
                  onClick={() => copyValue(userPassword, 'senha', 'senha')}
                >
                  {copiedField === 'senha' ? 'Copiado' : 'Copiar'}
                </button>
              )}
            </div>
          </div>
        </section>
      </div>

      <div className={styles.footer}>
        <button type='button' className='btn btn-lg btn-primary' onClick={handleClose}>
          Fechar
          <KTSVG path='/media/icons/duotune/arrows/arr064.svg' className='svg-icon-3 ms-2 me-0' />
        </button>
      </div>
    </Modal>,
    modalsRoot
  )
}

export {ModalDetailsCreate}
